import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Button, Box, Slider, Typography, CircularProgress
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

// Relación de aspecto única usada en todo el front (cards de recetas, hero y menú semanal)
export const RECIPE_IMAGE_ASPECT = 4 / 3;

// Genera un blob recortado a partir de la imagen y el área de recorte (en píxeles)
const getCroppedBlob = (imageSrc, cropPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      // Limitamos el ancho máximo para no generar imágenes enormes
      const maxWidth = 1280;
      const scale = cropPixels.width > maxWidth ? maxWidth / cropPixels.width : 1;
      canvas.width = Math.round(cropPixels.width * scale);
      canvas.height = Math.round(cropPixels.height * scale);

      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('No se pudo generar la imagen recortada.'));
        },
        'image/jpeg',
        0.9
      );
    };
    image.onerror = () => reject(new Error('No se pudo cargar la imagen.'));
    image.src = imageSrc;
  });

const ImageCropDialog = ({
  open,
  imageSrc,
  aspect = RECIPE_IMAGE_ASPECT,
  onCancel,
  onConfirm,
  onError
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    try {
      setProcessing(true);
      setError('');
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      await onConfirm(blob);
    } catch (e) {
      console.error(e);
      const errorMessage = e.message || 'No se pudo preparar la imagen.';
      setError(errorMessage);
      onError?.(e);
    } finally {
      setProcessing(false);
    }
  };

  // Reiniciar estado al cerrar
  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setError('');
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Ajustar foto de la receta</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Arrastra para encuadrar y usa el zoom para que encaje en el marco.
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 320,
            bgcolor: '#0f172a',
            borderRadius: '12px',
            overflow: 'hidden'
          }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              showGrid
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <ZoomOutIcon sx={{ color: '#64748b' }} />
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={(_, value) => setZoom(value)}
            sx={{ color: '#667eea' }}
          />
          <ZoomInIcon sx={{ color: '#64748b' }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit" disabled={processing}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={processing || !croppedAreaPixels}
          startIcon={processing ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: '10px', bgcolor: '#667eea' }}
        >
          {processing ? 'Procesando...' : 'Aplicar y subir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
